import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { MISSION_PATHS } from "@/data/site";
import { applyMission } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const emptyForm = {
  path: "reader",
  full_name: "",
  email: "",
  phone: "",
  country: "",
  state_city: "",
  profession: "",
  organisation: "",
  languages_known: "",
  area_of_interest: "",
  experience: "",
  contribution: "",
  consent_contact: false,
  consent_updates: false,
};

// Map homepage participation card ids to the closest detailed path id.
const CARD_TO_PATH = { member: "reader", volunteer: "volunteer", partner: "researcher", support: "support" };

export default function JoinMission() {
  useDocumentTitle("Join the Mission");
  const [params] = useSearchParams();
  const [form, setForm] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const p = params.get("path");
    if (p) {
      const resolved = CARD_TO_PATH[p] || p;
      setForm(f => ({ ...f, path: resolved }));
      setTimeout(() => {
        document.getElementById("mission-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [params]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_contact) return toast.error("Please consent to be contacted before submitting.");
    setLoading(true);
    try {
      await applyMission(form);
      setSubmitted(true);
      toast.success("Application received. Thank you for joining the mission.");
    } catch {
      toast.error("Submission failed. Please try again.");
    } finally { setLoading(false); }
  };

  const showOrg = ["researcher", "educator", "library", "invite_author", "sponsor"].includes(form.path);
  const showLangs = ["translator", "reader", "researcher"].includes(form.path);
  const showExperience = ["researcher", "translator", "volunteer", "educator"].includes(form.path);

  return (
    <div data-testid="join-mission-page">
      <section className="container-wide pt-16 pb-16">
        <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>Participate</div>
        <h1 className="mt-3 font-heading text-5xl sm:text-6xl leading-[1.02]" style={{ color: "var(--gi-maroon)" }}>Join the Mission</h1>
        <div className="hair-divider my-8 max-w-md" />
        <p className="max-w-3xl text-lg leading-relaxed" style={{ color: "var(--gi-charcoal-2)" }}>
          Help explore, preserve and share India's civilizational knowledge. Choose the path that best reflects how you would like to participate. Personal information you submit is not displayed publicly.
        </p>
      </section>

      <section className="container-wide pb-20">
        <div className="font-accent text-[10px] mb-6" style={{ color: "var(--gi-maroon)" }}>Choose your path</div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="paths-grid">
          {MISSION_PATHS.map((p, i) => (
            <button
              key={p.id}
              onClick={() => { set("path", p.id); document.getElementById("mission-form")?.scrollIntoView({ behavior: "smooth" }); }}
              className="text-left p-6 border transition-colors"
              style={{
                borderColor: form.path === p.id ? "var(--gi-maroon)" : "var(--gi-border)",
                background: form.path === p.id ? "var(--gi-ivory-2)" : "#fff",
              }}
              data-testid={`path-${p.id}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>{String(i + 1).padStart(2, "0")}</span>
                {form.path === p.id && <Check size={16} style={{ color: "var(--gi-maroon)" }} />}
              </div>
              <div className="mt-4 font-heading text-xl" style={{ color: "var(--gi-charcoal)" }}>{p.label}</div>
            </button>
          ))}
        </div>
      </section>

      <section id="mission-form" className="py-24" style={{ background: "var(--gi-ivory-2)" }}>
        <div className="container-wide max-w-4xl">
          <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>
            Application · {MISSION_PATHS.find(p => p.id === form.path)?.label}
          </div>
          <h2 className="mt-3 font-heading text-4xl leading-[1.05]" style={{ color: "var(--gi-maroon)" }}>Your Details</h2>
          <div className="hair-divider my-8 max-w-xs" />

          {submitted ? (
            <div className="p-10 bg-white border" style={{ borderColor: "var(--gi-gold)" }} data-testid="mission-submitted">
              <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>Thank you</div>
              <div className="mt-3 font-heading text-3xl" style={{ color: "var(--gi-maroon)" }}>Application Received</div>
              <p className="mt-4 text-base" style={{ color: "var(--gi-charcoal-2)" }}>
                We appreciate your interest in joining the mission. A member of the Glories of India Publications team will get in touch with next steps.
              </p>
              <button onClick={() => { setForm(emptyForm); setSubmitted(false); }} className="btn-outline mt-8" data-testid="mission-submit-another">Submit another application</button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-5" data-testid="mission-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name" required value={form.full_name} onChange={v => set("full_name", v)} testId="mission-name" />
                <Field label="Email" type="email" required value={form.email} onChange={v => set("email", v)} testId="mission-email" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Mobile Number" value={form.phone} onChange={v => set("phone", v)} testId="mission-phone" />
                <Field label="Country" value={form.country} onChange={v => set("country", v)} testId="mission-country" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="State and City" value={form.state_city} onChange={v => set("state_city", v)} testId="mission-state-city" />
                <Field label="Profession" value={form.profession} onChange={v => set("profession", v)} testId="mission-profession" />
              </div>
              {showOrg && <Field label="Organisation / Institution" value={form.organisation} onChange={v => set("organisation", v)} testId="mission-org" />}
              {showLangs && <Field label="Languages Known" value={form.languages_known} onChange={v => set("languages_known", v)} testId="mission-languages" />}
              <Field label="Area of Interest" value={form.area_of_interest} onChange={v => set("area_of_interest", v)} testId="mission-interest" />
              {showExperience && <TextArea label="Relevant Experience" value={form.experience} onChange={v => set("experience", v)} testId="mission-experience" />}
              <TextArea label="How would you like to contribute?" value={form.contribution} onChange={v => set("contribution", v)} testId="mission-contribution" />

              <div className="pt-2 space-y-3">
                <label className="flex items-start gap-3 text-sm" style={{ color: "var(--gi-charcoal-2)" }}>
                  <input type="checkbox" checked={form.consent_contact} onChange={e => set("consent_contact", e.target.checked)} className="mt-1" data-testid="mission-consent-contact" />
                  <span>I consent to be contacted regarding this application.</span>
                </label>
                <label className="flex items-start gap-3 text-sm" style={{ color: "var(--gi-charcoal-2)" }}>
                  <input type="checkbox" checked={form.consent_updates} onChange={e => set("consent_updates", e.target.checked)} className="mt-1" data-testid="mission-consent-updates" />
                  <span>I would also like to receive occasional research and publication updates.</span>
                </label>
              </div>

              <div className="pt-4">
                <button type="submit" disabled={loading} className="btn-maroon" data-testid="mission-submit-btn">
                  {loading ? "Submitting…" : "Submit Application"} <ArrowRight size={14} />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, value, onChange, required, type = "text", testId }) => (
  <div>
    <label className="gi-label">{label}{required && " *"}</label>
    <input className="gi-input" type={type} value={value} onChange={e => onChange(e.target.value)} required={required} data-testid={testId} />
  </div>
);

const TextArea = ({ label, value, onChange, testId }) => (
  <div>
    <label className="gi-label">{label}</label>
    <textarea className="gi-input" rows={4} value={value} onChange={e => onChange(e.target.value)} data-testid={testId} />
  </div>
);
